'use client';

import CancelIcon from '@hc-frontend/core-icons/kit/cancel';
import { Card, CardBody, CardHeader, Input } from '@hc-frontend/core-ui';
import { useSearchParams } from 'next/navigation';
import type { ChangeEventHandler } from 'react';
import { useRef, useState } from 'react';

import type { FilterGroupEntity } from '../../../domain';
import { ClearFiltersLink } from '../../atoms/clear-filters-link';
import { FilterGroup } from '../../molecules/filter-group';
import type {
  CarrierFilterProps,
  CarrierFilterResultsProps,
} from './carrier-filter.types';

function CarrierFilterResults({
  results,
  isLoading,
  error,
  onClick,
}: CarrierFilterResultsProps) {
  if (isLoading) {
    return <p className="hc-typography--paragraph-sm">Loading...</p>;
  }

  if (error) {
    return (
      <p className="hc-typography--paragraph-sm text-error-800">{error}</p>
    );
  }

  if (results.items.length === 0) {
    return (
      <p className="hc-typography--paragraph-sm">
        No results, try searching for a different carrier.
      </p>
    );
  }

  return <FilterGroup onClick={onClick} group={results} type="checkbox" />;
}

export function CarrierFilter({
  defaultCarriers,
  selectedCarriers,
  searchCarriers,
  userId,
  product,
}: CarrierFilterProps) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const searchParams = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [searchResults, setSearchResults] =
    useState<FilterGroupEntity>(defaultCarriers);

  const params = new URLSearchParams(searchParams.toString());

  const notSelectedCarriers = {
    ...searchResults,
    items: searchResults.items.filter(
      (item) => !params.has('carrier', item.value),
    ),
  };

  const executeSearch = async (value: string) => {
    setIsLoading(true);
    setError('');

    const result = await searchCarriers({
      search: value,
      limit: 5,
      userId,
      product,
    });

    setIsLoading(false);
    if (!result.success) {
      setError('An error happened searching for carriers.');
      return;
    }

    setSearchResults(result.data);
  };

  const handleSelectItem = () => {
    setIsLoading(false);
    setError('');
  };

  const handleInputSearch: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const { value } = e.target;
    setSearchValue(value);

    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (value === '') {
        setIsLoading(false);
        setError('');
        setSearchResults(defaultCarriers);
        return;
      }
      executeSearch(value);
    }, 500);
  };

  return (
    <Card className="w-full">
      <CardHeader className="relative">
        <p className="hc-typography--heading-four font-semibold">Carriers</p>
        <ClearFiltersLink
          id="clear-carrier-filter"
          className="absolute right-16-16 top-1/2 -translate-y-1/2"
          filterKeys={[defaultCarriers.key]}
          text="Clear"
          onClick={() => {
            setSearchValue('');
            setSearchResults(defaultCarriers);
          }}
        />
      </CardHeader>
      <CardBody className="px-12-24 py-16-16">
        <Input
          role="search"
          name="carrierSearch"
          placeholder="Search Carrier"
          className="mb-12-12 max-w-full"
          onChange={handleInputSearch}
          value={searchValue}
          icon={
            searchValue.length > 0 ? (
              <button
                type="button"
                aria-label="cancel search"
                onClick={() => {
                  setSearchValue('');
                  setSearchResults(defaultCarriers);
                }}
              >
                <CancelIcon />
              </button>
            ) : null
          }
        />

        <div className="hc-stack-layout--small">
          {selectedCarriers.items.length > 0 ? (
            <FilterGroup
              onClick={handleSelectItem}
              group={selectedCarriers}
              type="checkbox"
            />
          ) : null}

          {selectedCarriers.items.length > 0 &&
          notSelectedCarriers.items.length > 0 ? (
            <hr />
          ) : null}

          <CarrierFilterResults
            isLoading={isLoading}
            error={error}
            onClick={handleSelectItem}
            results={notSelectedCarriers}
          />
        </div>
      </CardBody>
    </Card>
  );
}
